import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { useElevenLabsTTS } from '@/hooks/useElevenLabsTTS';
import {
  ArrowLeft, Users, BookOpen, HelpCircle, Volume2, Loader2,
  Heart, Star, ChevronRight, ChevronLeft, Check, Lightbulb
} from 'lucide-react';

const CollaborativePage = () => {
  const { childId } = useParams();
  const { api, user } = useAuth();
  const { t } = useTranslation();
  const [child, setChild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('reading');
  const [sessionActive, setSessionActive] = useState(false);
  
  // Reading state
  const [currentStory, setCurrentStory] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [parentReading, setParentReading] = useState(true);
  
  // Quiz state
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [parentHelping, setParentHelping] = useState(false);
  const [teamScore, setTeamScore] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);

  // Word Building Game State
  const [wordLetters, setWordLetters] = useState([]);
  const [targetWord, setTargetWord] = useState('');
  const [builtWord, setBuiltWord] = useState('');
  const [wordScore, setWordScore] = useState(0);
  const [wordRound, setWordRound] = useState(1);

  const wordBuildingWords = [
    { word: 'CAT', hint: 'A furry pet that says meow' },
    { word: 'DOG', hint: 'A loyal pet that barks' },
    { word: 'SUN', hint: 'It shines bright in the sky' },
    { word: 'BALL', hint: 'You can throw and catch it' },
    { word: 'TREE', hint: 'It has leaves and grows tall' },
    { word: 'FISH', hint: 'It swims in water' },
    { word: 'BIRD', hint: 'It has wings and can fly' },
    { word: 'STAR', hint: 'It twinkles at night' },
  ];

  // Sample story for shared reading
  const sampleStory = {
    title: "The Little Star's Journey",
    pages: [
      {
        text: "Once upon a time, high up in the night sky, there lived a little star named Twinkle. Twinkle was the smallest star in the whole galaxy, but had the biggest dreams.",
        image: "⭐"
      },
      {
        text: "One night, Twinkle decided to go on an adventure. 'I want to see the Earth!' said Twinkle. All the other stars gasped. 'That's so far away!' they said.",
        image: "🌍"
      },
      {
        text: "But Twinkle was brave. The little star began the long journey, zooming past planets and dancing through asteroid belts. Each moment was more exciting than the last!",
        image: "🚀"
      },
      {
        text: "Finally, Twinkle reached Earth and saw something beautiful - children looking up at the night sky, making wishes on stars. Twinkle glowed brighter than ever with happiness.",
        image: "👧"
      },
      {
        text: "From that day on, Twinkle became the brightest star in the sky, reminding all children that even the smallest among us can do great things. The End.",
        image: "✨"
      }
    ],
    questions: [
      { question: "What was the star's name?", options: ["Sparkle", "Twinkle", "Shine", "Glitter"], answer: "Twinkle" },
      { question: "Where did Twinkle want to go?", options: ["Mars", "The Moon", "Earth", "The Sun"], answer: "Earth" },
      { question: "How did Twinkle feel at the end?", options: ["Sad", "Angry", "Happy", "Tired"], answer: "Happy" }
    ]
  };

  // Quiz questions for co-learning
  const coLearningQuestions = [
    { 
      question: "What is 3 + 4?", 
      options: ["5", "6", "7", "8"], 
      answer: "7",
      hint: "Count on your fingers: 3... then add 4 more!"
    },
    { 
      question: "Which animal says 'moo'?", 
      options: ["Dog", "Cat", "Cow", "Pig"], 
      answer: "Cow",
      hint: "It lives on a farm and gives us milk"
    },
    { 
      question: "What color do you get mixing red and blue?", 
      options: ["Green", "Purple", "Orange", "Brown"], 
      answer: "Purple",
      hint: "Think of grapes or plums!"
    },
    { 
      question: "How many days are in a week?", 
      options: ["5", "6", "7", "8"], 
      answer: "7",
      hint: "Start with Sunday and count to Saturday"
    }
  ];

  useEffect(() => {
    fetchChild();
  }, [childId]);

  const fetchChild = async () => {
    try {
      const res = await api.get(`/children/${childId}`);
      setChild(res.data);
    } catch (error) {
      console.error('Failed to fetch child:', error);
    } finally {
      setLoading(false);
    }
  };

  const { speak: speakText } = useElevenLabsTTS({ voice: 'nova', speed: 0.85 });

  const startWordBuildingSession = () => {
    const wordData = wordBuildingWords[Math.floor(Math.random() * wordBuildingWords.length)];
    setTargetWord(wordData.word);
    setBuiltWord('');
    setWordScore(0);
    setWordRound(1);
    // Shuffle letters
    const letters = wordData.word.split('').sort(() => Math.random() - 0.5);
    // Add some extra letters for challenge
    const extraLetters = ['A', 'E', 'I', 'O', 'U', 'S', 'T', 'R', 'N'].filter(l => !wordData.word.includes(l));
    const allLetters = [...letters, ...extraLetters.slice(0, 2)].sort(() => Math.random() - 0.5);
    setWordLetters(allLetters);
    setSessionActive(true);
    setActiveTab('wordbuilding');
  };

  const handleLetterClick = (letter, index) => {
    const newBuiltWord = builtWord + letter;
    setBuiltWord(newBuiltWord);
    
    // Remove letter from available letters
    const newLetters = [...wordLetters];
    newLetters.splice(index, 1);
    setWordLetters(newLetters);
    
    // Check if word is complete
    if (newBuiltWord === targetWord) {
      setWordScore(s => s + 100);
      setTeamScore(s => s + 100);
      toast.success('Great teamwork! Word complete!');
      
      // Next round
      setTimeout(() => {
        if (wordRound < 5) {
          setWordRound(r => r + 1);
          const wordData = wordBuildingWords[Math.floor(Math.random() * wordBuildingWords.length)];
          setTargetWord(wordData.word);
          setBuiltWord('');
          const letters = wordData.word.split('').sort(() => Math.random() - 0.5);
          const extraLetters = ['A', 'E', 'I', 'O', 'U', 'S', 'T', 'R', 'N'].filter(l => !wordData.word.includes(l));
          const allLetters = [...letters, ...extraLetters.slice(0, 2)].sort(() => Math.random() - 0.5);
          setWordLetters(allLetters);
        } else {
          endSession();
        }
      }, 1500);
    } else if (newBuiltWord.length === targetWord.length) {
      // Wrong word
      toast.error('Not quite right! Let\'s try again.');
      setTimeout(() => {
        setBuiltWord('');
        const letters = targetWord.split('').sort(() => Math.random() - 0.5);
        const extraLetters = ['A', 'E', 'I', 'O', 'U', 'S', 'T', 'R', 'N'].filter(l => !targetWord.includes(l));
        const allLetters = [...letters, ...extraLetters.slice(0, 2)].sort(() => Math.random() - 0.5);
        setWordLetters(allLetters);
      }, 1000);
    }
  };

  const resetWord = () => {
    setBuiltWord('');
    const letters = targetWord.split('').sort(() => Math.random() - 0.5);
    const extraLetters = ['A', 'E', 'I', 'O', 'U', 'S', 'T', 'R', 'N'].filter(l => !targetWord.includes(l));
    const allLetters = [...letters, ...extraLetters.slice(0, 2)].sort(() => Math.random() - 0.5);
    setWordLetters(allLetters);
  };

  const startReadingSession = () => {
    setCurrentStory(sampleStory);
    setCurrentPage(0);
    setParentReading(true);
    setSessionActive(true);
  };

  const startQuizSession = () => {
    setQuizQuestions(coLearningQuestions);
    setCurrentQuestionIndex(0);
    setTeamScore(0);
    setSessionActive(true);
  };

  const handleNextPage = () => {
    if (currentPage < currentStory.pages.length - 1) {
      setCurrentPage(p => p + 1);
      setParentReading(true);
    } else {
      // Story finished, start comprehension quiz
      setActiveTab('quiz');
      setQuizQuestions(currentStory.questions);
      setCurrentQuestionIndex(0);
      setTeamScore(0);
    }
  };

  const handleParentReadComplete = () => {
    setParentReading(false);
    speakText(currentStory.pages[currentPage].text);
  };

  const handleQuizAnswer = (answer) => {
    const correct = answer === quizQuestions[currentQuestionIndex].answer;
    setShowAnswer(true);
    
    if (correct) {
      setTeamScore(s => s + 100);
      toast.success(t('collaborative.great_teamwork'));
    } else {
      toast.error('Not quite, let\'s try the next one!');
    }

    setTimeout(() => {
      setShowAnswer(false);
      if (currentQuestionIndex < quizQuestions.length - 1) {
        setCurrentQuestionIndex(i => i + 1);
        setParentHelping(false);
      } else {
        // Quiz complete
        endSession();
      }
    }, 2000);
  };

  const endSession = async () => {
    setSessionActive(false);
    toast.success(`Great teamwork! Team score: ${teamScore}`);
    
    try {
      await api.post('/collaborative/complete', null, {
        params: {
          child_id: childId,
          activity_type: activeTab,
          team_score: teamScore
        }
      });
    } catch (error) {
      console.error('Failed to save session:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-green-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-green-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`/learn/${childId}`}>
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <Heart className="w-6 h-6 text-green-500" />
                <h1 className="font-fredoka text-xl font-bold text-green-800">
                  {t('collaborative.title')}
                </h1>
              </div>
            </div>
            {sessionActive && (
              <div className="flex items-center gap-2 bg-green-100 rounded-full px-4 py-2">
                <Star className="w-4 h-4 text-yellow-500" />
                <span className="font-bold text-green-800">{t('multiplayer.team_score')}: {teamScore}</span>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {!sessionActive ? (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="reading" className="gap-2" data-testid="tab-reading">
                <BookOpen className="w-4 h-4" /> {t('collaborative.shared_reading')}
              </TabsTrigger>
              <TabsTrigger value="quiz" className="gap-2" data-testid="tab-quiz">
                <HelpCircle className="w-4 h-4" /> {t('collaborative.co_learning')}
              </TabsTrigger>
              <TabsTrigger value="wordbuilding" className="gap-2" data-testid="tab-wordbuilding">
                <Star className="w-4 h-4" /> Word Building
              </TabsTrigger>
            </TabsList>

            <TabsContent value="reading">
              <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                <CardContent className="p-8 text-center">
                  <BookOpen className="w-16 h-16 mx-auto mb-4 text-amber-500" />
                  <h3 className="font-fredoka text-2xl font-bold text-amber-800 mb-2">
                    {t('collaborative.reading_together')}
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Read a story together! The parent reads aloud while the child follows along, 
                    then answer fun questions together.
                  </p>
                  <div className="flex items-center justify-center gap-4 mb-6">
                    <div className="text-center">
                      <div className="w-16 h-16 rounded-full bg-amber-200 flex items-center justify-center text-2xl mx-auto mb-2">
                        👨‍👩‍👧
                      </div>
                      <p className="text-sm text-slate-600">Parent reads</p>
                    </div>
                    <ChevronRight className="w-6 h-6 text-amber-400" />
                    <div className="text-center">
                      <div className="w-16 h-16 rounded-full bg-amber-200 flex items-center justify-center text-2xl mx-auto mb-2">
                        👂
                      </div>
                      <p className="text-sm text-slate-600">Child listens</p>
                    </div>
                    <ChevronRight className="w-6 h-6 text-amber-400" />
                    <div className="text-center">
                      <div className="w-16 h-16 rounded-full bg-amber-200 flex items-center justify-center text-2xl mx-auto mb-2">
                        ✋
                      </div>
                      <p className="text-sm text-slate-600">Answer together</p>
                    </div>
                  </div>
                  <Button 
                    onClick={startReadingSession}
                    className="bg-amber-500 hover:bg-amber-600"
                    data-testid="start-reading-btn"
                  >
                    Start Reading Session
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="quiz">
              <Card className="bg-gradient-to-br from-sky-50 to-blue-50 border-sky-200">
                <CardContent className="p-8 text-center">
                  <Users className="w-16 h-16 mx-auto mb-4 text-sky-500" />
                  <h3 className="font-fredoka text-2xl font-bold text-sky-800 mb-2">
                    {t('collaborative.co_learning')}
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Work together on quiz questions! Parents can provide hints 
                    to help children learn while having fun.
                  </p>
                  <div className="bg-sky-100 rounded-xl p-4 mb-6 text-left">
                    <h4 className="font-bold text-sky-800 mb-2">How it works:</h4>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>• Questions appear for the child to answer</li>
                      <li>• Parents can tap "Give Hint" to help</li>
                      <li>• Earn team points for correct answers</li>
                      <li>• Celebrate learning together!</li>
                    </ul>
                  </div>
                  <Button 
                    onClick={startQuizSession}
                    className="bg-sky-500 hover:bg-sky-600"
                    data-testid="start-quiz-btn"
                  >
                    Start Co-Learning Session
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="wordbuilding">
              <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
                <CardContent className="p-8 text-center">
                  <Star className="w-16 h-16 mx-auto mb-4 text-purple-500" />
                  <h3 className="font-fredoka text-2xl font-bold text-purple-800 mb-2">
                    Word Building Game
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Build words together! One person picks letters while the other guides.
                    It's a fun way to learn spelling!
                  </p>
                  <div className="bg-purple-100 rounded-xl p-4 mb-6 text-left">
                    <h4 className="font-bold text-purple-800 mb-2">How to play:</h4>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>• Look at the hint to guess the word</li>
                      <li>• Tap letters in the right order</li>
                      <li>• Parents can help sound out letters</li>
                      <li>• Complete 5 words to win!</li>
                    </ul>
                  </div>
                  <Button 
                    onClick={startWordBuildingSession}
                    className="bg-purple-500 hover:bg-purple-600"
                    data-testid="start-wordbuilding-btn"
                  >
                    Start Word Building
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        ) : activeTab === 'wordbuilding' && sessionActive ? (
          /* Word Building Game */
          <div className="space-y-6">
            {/* Progress */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-slate-500">
                <span>Round {wordRound} of 5</span>
                <span>Score: {wordScore}</span>
              </div>
              <Progress value={(wordRound / 5) * 100} className="h-2" />
            </div>

            {/* Word Building Area */}
            <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
              <CardContent className="p-8">
                {/* Hint */}
                <div className="text-center mb-6">
                  <p className="text-sm text-purple-600 mb-2">Hint:</p>
                  <p className="text-lg font-medium text-purple-800">
                    {wordBuildingWords.find(w => w.word === targetWord)?.hint}
                  </p>
                </div>

                {/* Built Word Display */}
                <div className="flex justify-center gap-2 mb-8">
                  {targetWord.split('').map((_, idx) => (
                    <div
                      key={idx}
                      className={`w-16 h-16 rounded-xl border-3 flex items-center justify-center text-3xl font-bold ${
                        builtWord[idx]
                          ? 'bg-purple-500 text-white border-purple-600'
                          : 'bg-white border-purple-200'
                      }`}
                    >
                      {builtWord[idx] || ''}
                    </div>
                  ))}
                </div>

                {/* Available Letters */}
                <div className="flex justify-center flex-wrap gap-3 mb-6">
                  {wordLetters.map((letter, idx) => (
                    <motion.button
                      key={`${letter}-${idx}`}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleLetterClick(letter, idx)}
                      className="w-14 h-14 rounded-xl bg-white border-2 border-purple-300 text-2xl font-bold text-purple-700 hover:bg-purple-100 shadow-md"
                      data-testid={`letter-${letter}-${idx}`}
                    >
                      {letter}
                    </motion.button>
                  ))}
                </div>

                {/* Reset Button */}
                <div className="text-center">
                  <Button 
                    variant="outline" 
                    onClick={resetWord}
                    className="border-purple-300 text-purple-600"
                  >
                    Reset Word
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* End Session Button */}
            <div className="text-center">
              <Button variant="outline" onClick={endSession}>
                End Session Early
              </Button>
            </div>
          </div>
        ) : activeTab === 'reading' && currentStory ? (
          /* Shared Reading Session */
          <div className="space-y-6">
            {/* Progress */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-slate-500">
                <span>Page {currentPage + 1} of {currentStory.pages.length}</span>
                <span>{currentStory.title}</span>
              </div>
              <Progress value={((currentPage + 1) / currentStory.pages.length) * 100} className="h-2" />
            </div>

            {/* Story Page */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentPage}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
              >
                <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                  <CardContent className="p-8">
                    {/* Story Image */}
                    <div className="text-center mb-6">
                      <span className="text-8xl">{currentStory.pages[currentPage].image}</span>
                    </div>

                    {/* Story Text */}
                    <div className="text-center mb-6">
                      <p className="text-xl leading-relaxed text-slate-700">
                        {currentStory.pages[currentPage].text}
                      </p>
                    </div>

                    {/* Parent Reading Indicator */}
                    {parentReading && (
                      <div className="bg-amber-100 rounded-xl p-4 text-center mb-4">
                        <p className="text-amber-800 font-medium">
                          📖 {t('collaborative.parent_reading')}
                        </p>
                        <Button 
                          onClick={handleParentReadComplete}
                          className="mt-2 bg-amber-500 hover:bg-amber-600"
                        >
                          <Volume2 className="w-4 h-4 mr-2" /> I've Finished Reading
                        </Button>
                      </div>
                    )}

                    {/* Child's Turn */}
                    {!parentReading && (
                      <div className="bg-green-100 rounded-xl p-4 text-center">
                        <p className="text-green-800 font-medium mb-2">
                          ✋ {t('collaborative.child_turn')}
                        </p>
                        <Button 
                          onClick={handleNextPage}
                          className="bg-green-500 hover:bg-green-600"
                        >
                          {currentPage < currentStory.pages.length - 1 ? (
                            <>Next Page <ChevronRight className="w-4 h-4 ml-1" /></>
                          ) : (
                            <>Finish Story <Check className="w-4 h-4 ml-1" /></>
                          )}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            </AnimatePresence>
          </div>
        ) : (
          /* Co-Learning Quiz Session */
          <div className="space-y-6">
            {/* Progress */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-slate-500">
                <span>Question {currentQuestionIndex + 1} of {quizQuestions.length}</span>
                <span>Team Score: {teamScore}</span>
              </div>
              <Progress value={((currentQuestionIndex + 1) / quizQuestions.length) * 100} className="h-2" />
            </div>

            {/* Question Card */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentQuestionIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <Card className="bg-gradient-to-br from-sky-50 to-blue-50 border-sky-200">
                  <CardContent className="p-6">
                    <h3 className="font-fredoka text-2xl font-bold text-sky-800 mb-6 text-center">
                      {quizQuestions[currentQuestionIndex]?.question}
                    </h3>

                    {/* Parent Hint Button */}
                    {!parentHelping && !showAnswer && (
                      <div className="text-center mb-4">
                        <Button 
                          variant="outline"
                          onClick={() => setParentHelping(true)}
                          className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                        >
                          <Lightbulb className="w-4 h-4" /> Parent: Give a Hint
                        </Button>
                      </div>
                    )}

                    {/* Hint Display */}
                    {parentHelping && !showAnswer && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 text-center"
                      >
                        <p className="text-amber-800">
                          💡 Hint: {quizQuestions[currentQuestionIndex]?.hint}
                        </p>
                      </motion.div>
                    )}

                    {/* Answer Options */}
                    <div className="grid grid-cols-2 gap-3">
                      {quizQuestions[currentQuestionIndex]?.options.map((option, i) => {
                        const isCorrect = option === quizQuestions[currentQuestionIndex].answer;
                        const showCorrect = showAnswer && isCorrect;
                        const showWrong = showAnswer && !isCorrect;
                        
                        return (
                          <motion.button
                            key={option}
                            whileHover={{ scale: showAnswer ? 1 : 1.02 }}
                            whileTap={{ scale: showAnswer ? 1 : 0.98 }}
                            onClick={() => !showAnswer && handleQuizAnswer(option)}
                            disabled={showAnswer}
                            className={`p-4 rounded-xl border-2 font-bold text-lg transition-all ${
                              showCorrect 
                                ? 'bg-green-100 border-green-500 text-green-800'
                                : showWrong
                                  ? 'bg-slate-50 border-slate-200 text-slate-400'
                                  : 'bg-white border-sky-200 hover:bg-sky-50 hover:border-sky-400'
                            }`}
                            data-testid={`quiz-option-${i}`}
                          >
                            {showCorrect && <Check className="w-5 h-5 inline mr-2" />}
                            {option}
                          </motion.button>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </AnimatePresence>

            {/* Cancel Button */}
            <div className="text-center">
              <Button variant="outline" onClick={() => setSessionActive(false)}>
                End Session
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CollaborativePage;
